import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import useEscapeClose from './useEscapeClose';

/**
 * "Ask before losing my edits" for the Edit forms (Max, 2026-08-19).
 *
 * Two rules that make the prompt tolerable rather than annoying:
 *  - it only appears when something actually changed. Opening a project to read it and closing
 *    again must never cost a click.
 *  - every way out of the form goes through it — Esc, the × and Cancel. A prompt that only one of
 *    the three respects is worse than none, because the other two still lose the work silently.
 *
 * The comparison is a snapshot taken the first time the form renders, against a canonical form of
 * the same object: keys sorted, and empty / null / false folded together. Without that folding a
 * field the form fills in lazily (`entity` appears only after a Case is picked) would count as an
 * edit the moment React added the key, and the prompt would fire on forms nobody touched.
 */
function canon(value) {
    if (value === null || value === undefined || value === false || value === '') return '';
    if (Array.isArray(value)) return value.map(canon);
    if (typeof value === 'object') {
        const out = {};
        Object.keys(value).sort().forEach((k) => {
            const c = canon(value[k]);
            if (c !== '') out[k] = c;            // an empty field and a missing field are the same thing
        });
        return out;
    }
    return String(value);
}

// Exported for the one form whose state lives in the page rather than in a modal component of
// its own (Edit Project Group), which therefore cannot snapshot itself on mount.
export const fingerprint = (value) => JSON.stringify(canon(value));

export function DiscardPrompt({ title, body, onKeep, onDiscard }) {
    const { t } = useTranslation();
    // Portalled to <body> so it never depends on where the calling form renders it — nested inside
    // a modal that is itself a stacking context, a plain child could paint underneath the form.
    // The `.st-page` wrapper comes along because the tracker's modal styling reads CSS custom
    // properties scoped to that class (--dark, --line); at <body> level they would resolve to
    // nothing and the dialog's header would render unstyled.
    return createPortal((
        <div className="st-page">
        <div className="st-modal-overlay st-overlay-top" role="dialog" aria-modal="true">
            <div className="st-modal st-modal-sm">
                <div className="st-modal-head">
                    <h3>{title || t('st.dgEditTitle')}</h3>
                </div>
                <div className="st-modal-body">
                    <p>{body || t('st.dgEditBody')}</p>
                </div>
                <div className="st-modal-foot st-form-actions">
                    <button type="button" className="btn btn-ghost btn-sm" onClick={onKeep}>{t('st.pfDiscardKeep')}</button>
                    <button type="button" className="btn btn-danger btn-sm" onClick={onDiscard}>{t('st.pfDiscardConfirm')}</button>
                </div>
            </div>
        </div>
        </div>
    ), document.body);
}

/**
 * @param snapshot  the form state to watch — any plain object/array
 * @param onClose   what closing the form actually does
 * @param opts.active  false while a save is in flight, so Esc cannot interrupt it
 * @param opts.extraDirty  a dirty signal the snapshot cannot see (e.g. "SKU rows were added")
 * @returns {{ requestClose, prompt, dirty }} — render `prompt`, and wire `requestClose` to Esc/×/Cancel
 */
export default function useDiscardGuard(snapshot, onClose, { active = true, extraDirty = false, title, body } = {}) {
    const initial = useRef(null);
    if (initial.current === null) initial.current = fingerprint(snapshot);
    const dirty = extraDirty || fingerprint(snapshot) !== initial.current;

    const [asking, setAsking] = useState(false);
    const requestClose = () => { if (dirty) setAsking(true); else onClose(); };

    // Registered in this order on purpose: the prompt joins the Esc stack above the form, so Esc
    // while the prompt is up answers the prompt instead of closing everything at once.
    useEscapeClose(requestClose, active);
    useEscapeClose(() => setAsking(false), asking);

    const prompt = asking ? (
        <DiscardPrompt
            title={title}
            body={body}
            onKeep={() => setAsking(false)}
            onDiscard={() => { setAsking(false); onClose(); }}
        />
    ) : null;

    return { requestClose, prompt, dirty };
}
